import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { mockAPI } from './mockAPI'
import './onboardingWizard.css'
import { firebaseAuth } from "../firebase/firebaseClient"

const PASSWORD_SPECIAL_RE = /[^A-Za-z0-9]/

function scorePasswordStrength(password) {
    let strength = 0
    if (password.length >= 8) strength++
    if (/[A-Z]/.test(password)) strength++
    if (/[a-z]/.test(password)) strength++
    if (/[0-9]/.test(password)) strength++
    if (PASSWORD_SPECIAL_RE.test(password)) strength++
    return strength
}

function ProgressBar({ step, total }) {
    const { t } = useTranslation()
    return (
        <div className="ob-progress">
            <div className="ob-progress-track">
                <div className="ob-progress-fill" style={{ width: `${(step / total) * 100}%` }} />
            </div>
            <span className="ob-progress-label">{t('onboarding.step', { step, total })}</span>
        </div>
    )
}

function Screen0({ data, onChange, onNext }) {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [passwordStrength, setPasswordStrength] = useState(0)
    const [showPassword, setShowPassword] = useState(false)

    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

    const handlePasswordChange = (value) => {
        onChange('password', value)
        setPasswordStrength(scorePasswordStrength(value))
    }

    const handleNext = async () => {
        if (!validateEmail(data.email)) { setError('Please enter a valid email address'); return }
        if (data.password.length < 8) { setError('Password must be at least 8 characters long'); return }
        if (passwordStrength < 5) { setError('Password must meet all requirements.'); return }

        setLoading(true)
        setError('')
        localStorage.removeItem('authToken')
        localStorage.removeItem('mockAuthToken')

        try {
            const existingUser = firebaseAuth.currentUser
            const email = String(data.email || '').trim()
            if (existingUser && String(existingUser.email || '').trim().toLowerCase() === email.toLowerCase()) {
                const idToken = await existingUser.getIdToken()
                onChange('userId', existingUser.uid)
                onChange('authToken', idToken)
                onNext(); return
            }
            const result = await mockAPI.createAccount({ email: data.email, password: data.password })
            onChange('userId', result.userId)
            onChange('authToken', result.authToken)
            if (result.registerWarning) setError(result.registerWarning)
            onNext()
        } catch (err) {
            setError(err.message || 'Failed to create account. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const isFormValid = validateEmail(data.email) && passwordStrength >= 5
    const continueBlockedReason = !validateEmail(data.email)
        ? t('onboarding.screen0.invalidEmail')
        : passwordStrength < 5 ? t('onboarding.screen0.passwordWeak') : ''

    const strengthLabel = passwordStrength <= 2
        ? t('onboarding.screen0.weak')
        : passwordStrength === 3 ? t('onboarding.screen0.fair')
            : t('onboarding.screen0.strong')

    return (
        <div className="ob-screen">
            <div className="ob-icon">👋</div>
            <h2 className="ob-title">{t('onboarding.screen0.title')}</h2>
            {error && <div className="ob-error">⚠️ {error}</div>}

            <div className="ob-field">
                <label>{t('onboarding.screen0.email')} <span className="ob-required">*</span></label>
                <input type="email" placeholder="your@email.com" value={data.email}
                       onChange={e => onChange('email', e.target.value)} disabled={loading} required />
                {data.email && !validateEmail(data.email) && (
                    <span className="ob-field-hint ob-field-hint--error">Invalid email format</span>
                )}
            </div>

            <div className="ob-field">
                <label>{t('onboarding.screen0.password')} <span className="ob-required">*</span></label>
                <div className="ob-password-wrapper">
                    <input type={showPassword ? 'text' : 'password'}
                           placeholder={t('onboarding.screen0.passwordPlaceholder')}
                           value={data.password} onChange={e => handlePasswordChange(e.target.value)}
                           disabled={loading} required />
                    <button type="button" className="ob-password-toggle"
                            onClick={() => setShowPassword(!showPassword)} disabled={loading}>
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                </div>

                {data.password && (
                    <div className="ob-password-strength">
                        <div className="ob-strength-meter">
                            {[1,2,3,4,5].map(i => (
                                <div key={i} className={`ob-strength-bar ${i <= passwordStrength ? 'ob-strength-bar--filled' : ''}`}
                                     style={{ backgroundColor: i <= passwordStrength
                                             ? (passwordStrength <= 2 ? '#e53e3e' : passwordStrength === 3 ? '#f6ad55' : '#38a169')
                                             : '#e2e8f0' }} />
                            ))}
                        </div>
                        <span className={`ob-strength-text ${passwordStrength <= 2 ? 'ob-strength-weak' : passwordStrength === 3 ? 'ob-strength-fair' : 'ob-strength-strong'}`}>
                            {strengthLabel}
                        </span>
                    </div>
                )}

                <div className="ob-field-hint">{t('onboarding.screen0.req1')}{data.password.length >= 8 && ' ✓'}</div>
                <div className="ob-field-hint">{t('onboarding.screen0.req2')}{/[A-Z]/.test(data.password) && /[a-z]/.test(data.password) && ' ✓'}</div>
                <div className="ob-field-hint">{t('onboarding.screen0.req3')}{/[0-9]/.test(data.password) && ' ✓'}</div>
                <div className="ob-field-hint">{t('onboarding.screen0.req4')}{PASSWORD_SPECIAL_RE.test(data.password) && ' ✓'}</div>
            </div>

            <div className="ob-terms">
                <p>
                    {t('onboarding.screen0.terms')}{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer">{t('onboarding.screen0.termsLink')}</a>
                    {' '}{t('onboarding.screen0.and')}{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer">{t('onboarding.screen0.privacyLink')}</a>.
                </p>
            </div>

            {!loading && continueBlockedReason && (
                <p className="ob-field-hint ob-field-hint--error" role="status">{continueBlockedReason}</p>
            )}

            <button className="ob-btn" onClick={handleNext} disabled={loading || !isFormValid}>
                {loading ? t('onboarding.screen0.creating') : t('onboarding.screen0.continue')}
            </button>

            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                <Link to="/login/" style={{ color: '#666', textDecoration: 'none', fontSize: '0.9rem' }}>
                    {t('onboarding.screen0.backToLogin')}
                </Link>
            </div>
        </div>
    )
}

function Screen1({ data, onChange, onNext, onBack, isEditMode = false }) {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const categories = ['Café', 'Restaurant', 'Beauty', 'Bubble Tea', 'Grocery', 'Other']

    const handlePlaceIdChange = (value) => {
        onChange('googlePlaceId', value)
        if (!String(value).trim()) onChange('freeVisitForReview', false)
    }

    const handleNext = async () => {
        setLoading(true); setError('')
        try {
            const googlePlaceId = String(data.googlePlaceId || '').trim()
            const result = await mockAPI.saveBusiness({
                businessName: data.businessName,
                address: data.address,
                category: data.category,
                phone: data.phone,
                googlePlaceId,
                freeVisitForReview: googlePlaceId ? Boolean(data.freeVisitForReview) : false,
            })
            onChange('businessId', result.businessId)
            onNext()
        } catch (err) { setError(err.message) }
        finally { setLoading(false) }
    }

    return (
        <div className="ob-screen">
            <div className="ob-icon">🏪</div>
            <h2 className="ob-title">{isEditMode ? t('onboarding.screen1.titleEdit') : t('onboarding.screen1.title')}</h2>
            <p className="ob-subtitle">{isEditMode ? t('onboarding.screen1.subtitleEdit') : t('onboarding.screen1.subtitle')}</p>
            {error && <div className="ob-error">⚠️ {error}</div>}

            <div className="ob-field">
                <label>{t('onboarding.screen1.businessName')} <span className="ob-required">*</span></label>
                <input type="text" placeholder={t('onboarding.screen1.businessNamePlaceholder')}
                       value={data.businessName} onChange={e => onChange('businessName', e.target.value)} disabled={loading} required />
            </div>

            <div className="ob-field">
                <label>{t('onboarding.screen1.address')} <span className="ob-required">*</span></label>
                <input type="text" placeholder={t('onboarding.screen1.addressPlaceholder')}
                       value={data.address} onChange={e => onChange('address', e.target.value)} disabled={loading} required />
                <span className="ob-field-hint">{t('onboarding.screen1.addressTip')}</span>
            </div>

            <div className="ob-field">
                <label>{t('onboarding.screen1.category')} <span className="ob-required">*</span></label>
                <select value={data.category} onChange={e => onChange('category', e.target.value)} disabled={loading} required>
                    <option value="">{t('onboarding.screen1.selectCategory')}</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>

            <div className="ob-field">
                <label>{t('onboarding.screen1.phone')} <span className="ob-required">*</span></label>
                <input type="tel" placeholder={t('onboarding.screen1.phonePlaceholder')}
                       value={data.phone} onChange={e => onChange('phone', e.target.value)} disabled={loading} required />
            </div>

            <div className="ob-field">
                <label>{t('onboarding.screen1.googlePlaceId')}</label>
                <input type="text" placeholder={t('onboarding.screen1.googlePlaceIdPlaceholder')}
                       value={data.googlePlaceId || ''} onChange={e => handlePlaceIdChange(e.target.value)}
                       disabled={loading} />
                <span className="ob-field-hint">{t('onboarding.screen1.googlePlaceIdHint')}</span>
            </div>

            {String(data.googlePlaceId || '').trim() && (
                <label className="ob-check ob-check--inline">
                    <input type="checkbox" checked={Boolean(data.freeVisitForReview)}
                           onChange={e => onChange('freeVisitForReview', e.target.checked)} disabled={loading} />
                    <span>{t('onboarding.screen1.freeVisitForReview')}</span>
                </label>
            )}

            <div className="ob-actions">
                <button className="ob-btn-back" onClick={onBack} disabled={loading}>{t('onboarding.back')}</button>
                <button className="ob-btn" onClick={handleNext} disabled={loading}>
                    {loading ? t('onboarding.saving') : t('onboarding.next')}
                </button>
            </div>
        </div>
    )
}

function Screen2({ data, onChange, onNext, onBack, isEditMode = false }) {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const handleNext = async () => {
        if (!data.businessId) { setError('Business ID is missing. Go back and save your business info first.'); return }
        setLoading(true); setError('')
        try {
            await mockAPI.saveReward({ businessId: data.businessId, visits: parseInt(data.visits, 10), rewardDescription: data.rewardDescription, rewardType: data.rewardType })
            onNext()
        } catch (err) { setError(err.message) }
        finally { setLoading(false) }
    }

    return (
        <div className="ob-screen">
            <div className="ob-icon">🎁</div>
            <h2 className="ob-title">{isEditMode ? t('onboarding.screen2.titleEdit') : t('onboarding.screen2.title')}</h2>
            <p className="ob-subtitle">{isEditMode ? t('onboarding.screen2.subtitleEdit') : t('onboarding.screen2.subtitle')}</p>
            {error && <div className="ob-error">⚠️ {error}</div>}

            <div className="ob-punchcard">
                <div className="ob-punchcard-preview">
                    <span>{t('onboarding.screen2.buy')}</span>
                    <input type="number" className="ob-inline-input" value={data.visits} min={1} max={50}
                           onChange={e => onChange('visits', e.target.value)} disabled={loading} />
                    <span>{t('onboarding.screen2.visits')}</span>
                    <input type="text" className="ob-inline-input ob-inline-wide" value={data.rewardDescription}
                           onChange={e => onChange('rewardDescription', e.target.value)} disabled={loading} />
                </div>
            </div>

            <div className="ob-field">
                <label>{t('onboarding.screen2.visitsLabel')}</label>
                <input type="number" value={data.visits} min={1} max={50}
                       onChange={e => onChange('visits', e.target.value)} disabled={loading} />
            </div>{/*
            <div className="ob-field">
                <label>{t('onboarding.screen2.rewardDesc')}</label>
                <input type="text" value={data.rewardDescription}
                       placeholder={t('onboarding.screen2.rewardDescPlaceholder')}
                       onChange={e => onChange('rewardDescription', e.target.value)} disabled={loading} />
            </div>
            */}
            <div className="ob-field">
                <label>{t('onboarding.screen2.rewardType')}</label>
                <input type="text" value={data.rewardType}
                       placeholder={t('onboarding.screen2.rewardTypePlaceholder')}
                       onChange={e => onChange('rewardType', e.target.value)} disabled={loading} />
            </div>

            {/* {data.rewardDescription= String(data.rewardType) } */}
            <div className="ob-actions">
                <button className="ob-btn-back" onClick={onBack} disabled={loading}>{t('onboarding.back')}</button>
                <button className="ob-btn" onClick={handleNext} disabled={loading}>
                    {loading ? t('onboarding.saving') : t('onboarding.next')}
                </button>
            </div>
        </div>
    )
}

function Screen3({ data, onNext, onBack }) {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const handleCheckout = async () => {
        setLoading(true); setError('')
        try {
            const result = await mockAPI.createCheckout({ businessId: data.businessId })
            if (result?.url) { window.location.href = result.url; return }
            onNext()
        } catch (err) { setError(err.message) }
        finally { setLoading(false) }
    }

    return (
        <div className="ob-screen">
            <div className="ob-icon">💳</div>
            <h2 className="ob-title">{t('onboarding.screen3.title')}</h2>
            <p className="ob-subtitle">{t('onboarding.screen3.subtitle')}</p>
            {error && <div className="ob-error">⚠️ {error}</div>}

            <div className="ob-stripe-box">
                <div className="ob-stripe-logo">stripe</div>
                <p>{t('onboarding.screen3.stripeSecure')}</p>
                <button className="ob-btn" onClick={handleCheckout} disabled={loading}>
                    {loading ? t('onboarding.screen3.loading') : t('onboarding.screen3.enterPayment')}
                </button>
                <span className="ob-hint">
                    {loading ? t('onboarding.screen3.creatingSession') : t('onboarding.screen3.mockHint')}
                </span>
            </div>

            <div className="ob-actions" style={{ marginTop: '1rem' }}>
                <button className="ob-btn-back" onClick={onBack} disabled={loading}>{t('onboarding.back')}</button>
            </div>
        </div>
    )
}

function Screen4({ data, onClose, isEditMode = false }) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [checklist, setChecklist] = useState({ nfc: false, staff: false, customers: false })
    const toggle = (key) => setChecklist(prev => ({ ...prev, [key]: !prev[key] }))

    const handleDashboard = async () => {
        if (!isEditMode) {
            try { await mockAPI.completeOnboarding(data.businessId) } catch (e) { console.error(e) }
            navigate('/login', { replace: true }); return
        }
        navigate('/merchant-profile/', { replace: true })
    }

    const checkItems = [
        { key: 'nfc', label: t('onboarding.screen4.check1') },
        { key: 'staff', label: t('onboarding.screen4.check2') },
        { key: 'customers', label: t('onboarding.screen4.check3') },
    ]

    return (
        <div className="ob-screen ob-screen--success">
            <div className="ob-success-badge">✅</div>
            <h2 className="ob-title">{isEditMode ? t('onboarding.screen4.titleEdit') : t('onboarding.screen4.title')}</h2>
            <p className="ob-subtitle">
                {isEditMode ? t('onboarding.screen4.subtitleEdit') : t('onboarding.screen4.subtitle')}{' '}
                <strong>{data.businessName || 'your business'}</strong>.
            </p>

            <div className="ob-summary">
                <div className="ob-summary-row">
                    <span>{t('onboarding.screen4.reward')}</span>
                    <strong>{t('onboarding.screen4.buy')} {data.visits} {t('onboarding.screen4.get')} {data.rewardDescription}</strong>
                </div>
                <div className="ob-summary-row">
                    <span>{t('onboarding.screen4.type')}</span>
                    <strong>{data.rewardType}</strong>
                </div>
                <div className="ob-summary-row">
                    <span>{t('onboarding.screen4.businessId')}</span>
                    <strong>{data.businessId}</strong>
                </div>
                <div className="ob-summary-row">
                    <span>{t('onboarding.screen4.nfcStand')}</span>
                    <strong>{t('onboarding.screen4.nfcDelivery')}</strong>
                </div>
            </div>

            <div className="ob-checklist">
                <h3>{t('onboarding.screen4.checklist')}</h3>
                {checkItems.map(({ key, label }) => (
                    <label key={key} className={`ob-check ${checklist[key] ? 'ob-check--done' : ''}`}>
                        <input type="checkbox" checked={checklist[key]} onChange={() => toggle(key)} />
                        <span>{label}</span>
                    </label>
                ))}
            </div>

            <button className="ob-btn" onClick={handleDashboard}>
                {isEditMode ? t('onboarding.screen4.backToProfile') : t('onboarding.screen4.goToDashboard')}
            </button>
        </div>
    )
}

function OnboardingWizard({ isOpen = true, onClose = () => {} }) {
    const navigate = useNavigate()
    const { i18n } = useTranslation() // ← add this

    const isFr = i18n.resolvedLanguage === 'fr'
    const toggleLanguage = () => {
        const nextLang = isFr ? 'en' : 'fr'
        i18n.changeLanguage(nextLang)
        localStorage.setItem('lang', nextLang)
    }
    const TOTAL = 5
    const [step, setStep] = useState(1)
    const [isEditMode, setIsEditMode] = useState(false)
    const [loading, setLoading] = useState(true)

    const [formData, setFormData] = useState({
        email: '', password: '', userId: null, authToken: null,
        businessId: null, businessName: '', address: '', category: '', phone: '',
        googlePlaceId: '', freeVisitForReview: false,
        visits: 6, rewardDescription: '1 free item', rewardType: 'Free item',
    })

    useEffect(() => {
        const checkExistingData = async () => {
            try {
                const user = firebaseAuth.currentUser
                if (!user) { setLoading(false); return }
                const result = await mockAPI.getUserBusinesses()
                if (result.businesses && result.businesses.length > 0) {
                    const business = result.businesses[0]
                    setIsEditMode(true)
                    setFormData(prev => ({
                        ...prev,
                        businessId: business.business_id,
                        businessName: business.business_name,
                        address: business.address,
                        category: business.category,
                        phone: business.phone,
                        googlePlaceId: business.google_place_id || business.googlePlaceId || '',
                        freeVisitForReview: Boolean(business.free_visit_for_review ?? business.freeVisitForReview),
                        visits: business.visits || 6,
                        rewardDescription: business.reward_description || '1 free item',
                        rewardType: business.reward_type || 'Free item',
                    }))
                    setStep(2)
                }
                setStep(2)
            } catch (error) {
                if (firebaseAuth.currentUser) setStep(2)
            } finally {
                setLoading(false)
            }
        }
        checkExistingData()
    }, [])

    const onChange = (key, value) => setFormData(prev => ({ ...prev, [key]: value }))
    const next = () => setStep(s => Math.min(s + 1, TOTAL))
    const back = () => setStep(s => Math.max(s - 1, 1))

    if (!isOpen || loading) return null

    return (
        <div className="ob-overlay" role="dialog" aria-modal="true" aria-label="Business onboarding">

            <div className="ob-modal">
                <button
                    type="button"
                    className={`lang-switch ${isFr ? 'fr' : 'en'}`}
                    onClick={toggleLanguage}
                    style={{ position: 'absolute', top: '1rem', right: '1rem' }}
                >
                    <span className="lang-switch-circle">{isFr ? 'FR' : 'EN'}</span>
                </button>
                <ProgressBar step={isEditMode ? step - 1 : step} total={isEditMode ? TOTAL - 1 : TOTAL} />
                {!isEditMode && step === 1 && <Screen0 data={formData} onChange={onChange} onNext={next} />}
                {step === 2 && <Screen1 data={formData} onChange={onChange} onNext={next} onBack={isEditMode ? () => navigate('/merchant-profile/') : back} isEditMode={isEditMode} />}
                {step === 3 && <Screen2 data={formData} onChange={onChange} onNext={next} onBack={back} isEditMode={isEditMode} />}
                {step === 4 && <Screen3 data={formData} onNext={next} onBack={back} />}
                {step === 5 && <Screen4 data={formData} onClose={onClose} isEditMode={isEditMode} />}
            </div>
        </div>
    )
}

export default OnboardingWizard